import { useHits } from "react-instantsearch";
import { useMemo } from "react";
import type {
  HighlightResult,
  HighlightResultNormalised,
  HitAttributeHighlightResult,
} from "components/layout/Search/Results/components/Highlight";
import type { HitKeys } from "@/components/layout/Search/Results/models/hit";

/**
 * Sorting function to ensure full matches are always prioritised, will
 * otherwise use the most complete match based on the number of matching
 * chars
 */
const byMatchPercentage = (
  a: HitAttributeHighlightResult,
  b: HitAttributeHighlightResult,
) => {
  if (a.matchLevel === "full" && b.matchLevel !== "full") {
    return 1;
  }

  if (a.matchLevel !== "full" && b.matchLevel === "full") {
    return -1;
  }

  return b.matchedWords.join("").length - a.matchedWords.join("").length;
};

export const useNormalisedHits = <T>() => {
  const { items, ...rest } = useHits<
    T & {
      _highlightResult?: HighlightResult;
      key: HitKeys;
    }
  >();

  const normalisedItems = useMemo(
    () =>
      (items || []).map((item) => {
        // highlights can be arrays or objects, we do this to always send an array
        item.key;
        return {
          ...item,
          normalisedHighlights: Object.fromEntries(
            Object.entries(item._highlightResult || {}).map(([key, value]) => {
              return [
                key,
                Array.isArray(value)
                  ? value.sort((a, b) => {
                      return byMatchPercentage(
                        a as HitAttributeHighlightResult,
                        b as HitAttributeHighlightResult,
                      );
                    })
                  : [value],
              ];
            }),
          ) as HighlightResultNormalised,
        };
      }),
    [items],
  );

  return { items: normalisedItems, ...rest };
};
